import React, { Suspense, useEffect, useMemo, useState } from "react";
import "./App.css";
import { Navigate, Route, Routes } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { AppDispatch, RootState } from "./store/store";
import { useDispatch, useSelector } from "react-redux";
import Layout from "./shared/layout/Layout";
import { SnackbarProvider } from "notistack";
import { SnackbarUtilsConfigurator } from "./shared/functions/snackbarUtils";
import { AuthService } from "./authentification/service";
import i18n from "./utils/localization/i18n";
import authSlice from "./store/slices/auth.slice";
import { fetchEnums } from "./store/slices/enums.slice";
import { handleError } from "./shared/functions/handleError";
import {
  getRoutesForRoles,
  getSidebarRoutes,
  publicRoutes,
} from "./utils/routes";
import BugCatch, { bugCatchEnabled } from './bugcatch';

const Spinner = () => (
  <div
    className="d-flex justify-content-center align-items-center"
    style={{ height: "100vh" }}
  >
    <div className="spinner-border text-primary" role="status" />
  </div>
);

const NoAccess = () => {
  const { t } = useTranslation();
  return (
    <div className="d-flex justify-content-center align-items-center p-5">
      <p className="text-muted mb-0">{t("general.noAccess")}</p>
    </div>
  );
};

function App() {
  const { isAuthenticated } = useSelector((state: RootState) => state.auth);
  const jwtToken = localStorage.getItem("token");
  const [isLoading, setIsLoading] = useState(true);
  const dispatch = useDispatch<AppDispatch>();
  const { userData } = useSelector((state: RootState) => state.auth);

  const userRoles = userData?.roles;
  const userRoutes = useMemo(() => getRoutesForRoles(userRoles), [userRoles]);
  const sidebarRoutes = useMemo(() => getSidebarRoutes(userRoles), [userRoles]);
  useEffect(() => {
    if (jwtToken) {
      setIsLoading(true);
      Promise.all([AuthService.me(), dispatch(fetchEnums())])
        .then(async ([userResponse]) => {
          // Respect any language the user previously selected in localStorage.
          // Only overwrite the stored language when the server explicitly returns a language.
          const storedLang =
            ((): string | null => {
              try {
                return (
                  localStorage.getItem("language") ||
                  localStorage.getItem("i18nextLng") ||
                  null
                );
              } catch (e) {
                return null;
              }
            })();
          const serverLang = userResponse?.language;
          const langToUse = serverLang ?? storedLang ?? "gb";

          if (serverLang) {
            try {
              localStorage.setItem("language", serverLang);
            } catch (e) { }
          }
          bugCatchEnabled && BugCatch.setUser({
            id: String(userResponse.id),
            email: userResponse.email,
            username: userResponse.username,
          });
          await i18n.changeLanguage(langToUse);
          dispatch(
            authSlice.actions.setLoginState({
              userData: userResponse,
              token: jwtToken,
              isAuthenticated: true,
            }),
          );
        })
        .catch(handleError)
        .finally(() => setIsLoading(false));
    } else {
      setIsLoading(false);
    }
  }, [dispatch, jwtToken]);

  return (
    <SnackbarProvider
      maxSnack={3}
      Components={
        {
          // success: StyledMaterialDesignContent,
          // error: StyledMaterialDesignContent,
        }
      }
      anchorOrigin={{
        vertical: "top",
        horizontal: "right",
      }}
    >
      <SnackbarUtilsConfigurator />
      {isLoading ? (
        <Spinner />
      ) : (
        <div className="page">
          {isAuthenticated ? (
            <Layout routes={sidebarRoutes}>
              <Suspense fallback={<Spinner />}>
                {userRoutes.length ? (
                  <Routes>
                    {userRoutes.map((route) => (
                      <Route
                        path={route.path}
                        element={route.element}
                        key={route.path}
                      />
                    ))}
                    <Route path="*" element={<Navigate to="/dashboard" />} />
                  </Routes>
                ) : (
                  // A role the frontend does not know about matches no route, so
                  // the catch-all redirect above would bounce forever.
                  <NoAccess />
                )}
              </Suspense>
            </Layout>
          ) : (
            <Suspense fallback={<Spinner />}>
              <Routes>
                {publicRoutes.map((route) => (
                  <Route
                    path={route.path}
                    element={route.element}
                    key={route.path}
                  />
                ))}
                <Route path="*" element={<Navigate to="/" />} />
              </Routes>
            </Suspense>
          )}
        </div>
      )}
    </SnackbarProvider>
  );
}

export default App;
